import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Main from "./Main";
import { makeCompanyNav } from "./nav";
import {
  listWorkspaceNotes,
  createWorkspaceNote,
  updateWorkspaceNote,
  deleteWorkspaceNote,
} from "../services/server";
import "./css/workspace-tools.css";

const SCOPES = [
  { value: "general", label: "Allmän" },
  { value: "buyer", label: "Köpare" },
  { value: "seller", label: "Säljare" },
  { value: "valuation", label: "Värdering" },
  { value: "im", label: "PM" },
  { value: "dd", label: "DD" },
];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("sv-SE", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Notes() {
  const { company } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");

  const [draftBody, setDraftBody] = useState("");
  const [draftScope, setDraftScope] = useState("general");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await listWorkspaceNotes(company, scopeFilter ? { scope: scopeFilter } : {});
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setErr(e?.message || "Kunde inte ladda anteckningar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (company) load(); /* eslint-disable-next-line */ }, [company, scopeFilter]);

  const onCreate = async (e) => {
    e.preventDefault();
    const body = draftBody.trim();
    if (!body || posting) return;
    setPosting(true);
    try {
      const created = await createWorkspaceNote(company, { body, scope: draftScope });
      setItems((prev) => [created, ...prev]);
      setDraftBody("");
    } catch (e2) {
      setErr(e2?.message || "Kunde inte spara anteckning");
    } finally {
      setPosting(false);
    }
  };

  const togglePin = async (note) => {
    try {
      const updated = await updateWorkspaceNote(company, note.id, { pinned: !note.pinned });
      setItems((prev) =>
        [...prev.map((n) => (n.id === note.id ? updated : n))]
          .sort((a, b) => {
            if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
            return new Date(b.created_at) - new Date(a.created_at);
          })
      );
    } catch (e) {
      setErr(e?.message || "Kunde inte fästa");
    }
  };

  const onDelete = async (note) => {
    if (!window.confirm("Ta bort anteckning?")) return;
    try {
      await deleteWorkspaceNote(company, note.id);
      setItems((prev) => prev.filter((n) => n.id !== note.id));
    } catch (e) {
      setErr(e?.message || "Kunde inte ta bort");
    }
  };

  const grouped = useMemo(() => {
    const pinned = items.filter((n) => n.pinned);
    const rest = items.filter((n) => !n.pinned);
    return { pinned, rest };
  }, [items]);

  return (
    <Main navItems={makeCompanyNav(company)}>
      <div className="wt-page">
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <Link className="wt-btn wt-btn--ghost" to={`/tasks/${company}`}>Tasks</Link>
          <span className="wt-btn wt-btn--primary">Anteckningar</span>
        </div>
        <h1 className="wt-page__title">Tasks & anteckningar</h1>
        <p className="wt-page__subtitle">
          Snabba anteckningar per workspace — köparedialog, kommentarer från
          möten, sådant ni annars förlorar i Outlook eller OneNote.
        </p>

        <section className="wt-card">
          <h3 className="wt-card__title">Ny anteckning</h3>
          <form onSubmit={onCreate}>
            <textarea
              className="wt-textarea"
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              placeholder="t.ex. Köpare A orolig för kundkoncentration — vill se top-10-fördelning före LOI."
              rows={3}
            />
            <div className="wt-form-row" style={{ marginTop: 8 }}>
              <select
                className="wt-select"
                value={draftScope}
                onChange={(e) => setDraftScope(e.target.value)}
              >
                {SCOPES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <button
                type="submit"
                className="wt-btn wt-btn--primary"
                disabled={posting || !draftBody.trim()}
              >
                {posting ? "Sparar…" : "Spara"}
              </button>
            </div>
          </form>
        </section>

        <section className="wt-card" style={{ marginTop: 16 }}>
          <h3 className="wt-card__title">
            Alla anteckningar
            <select
              className="wt-select"
              style={{ width: "auto", minWidth: 140 }}
              value={scopeFilter}
              onChange={(e) => setScopeFilter(e.target.value)}
            >
              <option value="">Alla scopes</option>
              {SCOPES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </h3>

          {loading && <p className="wt-empty">Laddar…</p>}
          {!loading && err && <p className="wt-empty">{err}</p>}
          {!loading && !err && items.length === 0 && (
            <p className="wt-empty">Inga anteckningar ännu.</p>
          )}

          {grouped.pinned.map((n) => (
            <NoteCard
              key={n.id}
              note={n}
              onPin={() => togglePin(n)}
              onDelete={() => onDelete(n)}
            />
          ))}
          {grouped.rest.map((n) => (
            <NoteCard
              key={n.id}
              note={n}
              onPin={() => togglePin(n)}
              onDelete={() => onDelete(n)}
            />
          ))}
        </section>
      </div>
    </Main>
  );
}

function NoteCard({ note, onPin, onDelete }) {
  const scopeLabel = SCOPES.find((s) => s.value === note.scope)?.label || note.scope;
  return (
    <div className={`wt-note ${note.pinned ? "wt-note--pinned" : ""}`}>
      <div className="wt-note__header">
        <div>
          <span className="wt-note__author">{note.author?.name || "Okänd"}</span>
          <span> · {fmtDate(note.created_at)}</span>
        </div>
        <div className="wt-row-actions">
          <span className="wt-note__scope">{scopeLabel}</span>
          <button type="button" className="wt-btn wt-btn--ghost" onClick={onPin}>
            {note.pinned ? "Lossa" : "Fäst"}
          </button>
          <button type="button" className="wt-btn wt-btn--ghost wt-btn--danger" onClick={onDelete}>
            Ta bort
          </button>
        </div>
      </div>
      <p className="wt-note__body">{note.body}</p>
    </div>
  );
}
